import apiService from './api';

export interface CartItem {
  id: number;
  name: string;
  sku: string;
  quantity: number;
  original_price: number;
  final_price: number;
  has_discount: boolean;
  item_total: number;
}

export interface CartCalculation {
  items: CartItem[];
  subtotal: number;
  shipping_cost: number;
  tax_amount: number;
  campaign_discount: number;
  campaign_discount_amount: number;
  total_amount: number;
  breakdown: {
    discounted_products_total: number;
    non_discounted_total: number;
    campaign_applied_to: number;
  };
}

export interface CampaignValidation {
  valid: boolean;
  message: string;
  discount_percentage: number;
  discount_amount: number;
}

export interface CartItemInput {
  id: number;
  quantity: number;
}

class PricingService {
  /**
   * Calculate secure cart totals from the backend
   */
  async calculateCart(items: CartItemInput[], shippingMethod: 'delivery' | 'pickup' = 'delivery', campaignCode?: string): Promise<CartCalculation> {
    const payload: any = {
      items,
      shipping_method: shippingMethod
    };

    if (campaignCode && campaignCode.trim()) {
      payload.campaign_code = campaignCode.trim().toUpperCase();
    }

    return await apiService.post('/pricing/calculate-cart', payload);
  }

  /**
   * Validate a campaign code securely
   */
  async validateCampaignCode(code: string, cartTotal: number, hasDiscountedProducts: boolean): Promise<CampaignValidation> {
    return await apiService.post('/pricing/validate-campaign', {
      code: code.trim().toUpperCase(),
      cart_total: cartTotal,
      has_discounted_products: hasDiscountedProducts
    });
  }

  /**
   * Get products with secure pricing
   */
  async getProducts(params?: { category?: string; featured?: boolean; limit?: number; offset?: number }) {
    const queryParams = new URLSearchParams();
    
    if (params?.category) queryParams.append('category', params.category);
    if (params?.featured !== undefined) queryParams.append('featured', params.featured.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const url = `/pricing/products${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return await apiService.get(url);
  }

  /**
   * Format currency for Swedish locale
   */
  formatSEK(amount: number): string {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK'
    }).format(amount);
  }
}

export default new PricingService();